import {AiTaggingTemplateDefinition} from '@frontapp/workflows/rules';
import React, {FC, useEffect, useRef, useState} from 'react';
import styled, {css, useTheme} from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {ProgressBar} from '../../../libs/shared/ui/src/components/progressBar';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../libs/shared/ui/src/components/utility/skeleton';
import {VisuallyHidden} from '../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';
import {getColorHighlight} from '../../helpers/models/highlightHelpers';
import {TagModel} from '../../models/api/tagModel';
import {AI_TAGGING_SAMPLES_NEEDED} from '../aiTagging/aiTaggingConstants';
import {ConversationHeaderPill} from '../inboxes/viewer/conversation/header/conversationHeaderPill';
import {ConversationHeaderTagPillContent} from '../inboxes/viewer/conversation/header/tag/conversationHeaderTagPillContent';

/*
 * Props.
 */

export interface AiHarvestingContentHeaderProps {
  tag: TagModel | undefined;
  aiTaggingTemplate: AiTaggingTemplateDefinition | undefined;
  onCancel: () => void;
}

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
`;

const StyledTitleH2 = styled.h2`
  // Remove the default margin-bottom from the <h2> tag.
  margin: 0;

  color: ${(p) => p.theme.greys.shade90};

  font-size: ${fontSizes.huge};
  font-weight: ${fontWeights.semibold};
  line-height: 24px;
  margin-bottom: 18px;
`;

const StyledTagSubheaderDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding-bottom: 4px;
`;

const StyledProgressBarContainerDiv = styled.div`
  margin-bottom: -8px;
`;

const StyledProgressTextDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.normal};
`;

const StyledPillWrapperDiv = styled.div`
  flex-shrink: 0;
`;

const StyledConversationHeaderPill = styled(ConversationHeaderPill)`
  border-radius: 4px;
  align-self: center;
`;

const StyledTagDescriptionButton = styled(ChromelessButton)`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};
  line-height: 21px;
  margin-bottom: 17px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: flex-start;

  border-radius: 8px;
  padding: 4px 8px;
  margin-left: -4px;

  &:hover {
    background-color: ${(p) => p.theme.greys.shade20};
  }
`;

const StyledTagDescriptionIcon = styled(Icon)<{$isDescriptionExpanded: boolean}>`
  ${(p) =>
    p.$isDescriptionExpanded
      ? css`
          align-self: flex-start;
          margin-top: 2px;
          rotate: 180deg;
        `
      : css`
          align-self: center;
        `}
`;

const StyledTagDescriptionDiv = styled.div<{$isDescriptionExpanded: boolean}>`
  align-self: center;
  ${(p) => !p.$isDescriptionExpanded && ellipsis()}
`;

const StyledHeaderButton = styled(ChromelessButton)`
  padding: 5px;
  position: absolute;
  top: 14px;
  right: -14px;

  border-radius: 8px;
  color: ${(p) => p.theme.greys.shade80};

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
    color: ${(p) => p.theme.greys.shade80};
  }
`;

const StyledHeaderStaticPartDiv = styled.div`
  background-color: ${(p) => p.theme.greys.white};
  position: sticky;
  top: 0;
  padding-top: 30px;
`;

/*
 * Component.
 */

export const AiHarvestingContentHeader: FC<AiHarvestingContentHeaderProps> = ({
  tag,
  aiTaggingTemplate,
  onCancel,
}) => {
  const theme = useTheme();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [shouldRenderFullDescription, setShouldRenderFullDescription] = useState(() =>
    Boolean((tag?.description?.length || 0) > 0),
  );

  const descriptionRef = useRef<HTMLDivElement>(null);
  const previousTag = usePrevious(tag);

  useEffect(() => {
    if (!tag || tag.id === previousTag?.id) {
      return;
    }

    if (!descriptionRef.current) {
      return;
    }

    if (isDescriptionExpanded) {
      return;
    }

    const isTruncated = descriptionRef.current.scrollWidth > descriptionRef.current.offsetWidth;
    // Enable the expandable description if description is too long to fit on one line
    setShouldRenderFullDescription(!isTruncated);
  }, [isDescriptionExpanded, previousTag, tag]);

  if (!tag || !aiTaggingTemplate) {
    return renderLoadingState(onCancel);
  }

  const color = getColorHighlight(tag);
  const progress =
    aiTaggingTemplate.training_sets.find((ts) => ts.resource_id === tag.id)?.sample_message_ids?.length ?? 0;

  return (
    <StyledWrapperDiv>
      <StyledHeaderStaticPartDiv>
        <StyledHeaderButton key="close" onClick={onCancel}>
          <Icon name="close" />
          <VisuallyHidden>
            <Trans id="dialogheader-close">Close</Trans>
          </VisuallyHidden>
        </StyledHeaderButton>
        <StyledTitleH2>{renderTitle()}</StyledTitleH2>
        <StyledTagSubheaderDiv>
          <StyledPillWrapperDiv>
            <StyledConversationHeaderPill size={VisualSizesEnum.MEDIUM} color={color}>
              <ConversationHeaderTagPillContent size={VisualSizesEnum.MEDIUM} tagsPath={[tag]} />
            </StyledConversationHeaderPill>
          </StyledPillWrapperDiv>
        </StyledTagSubheaderDiv>
      </StyledHeaderStaticPartDiv>
      <StyledTagDescriptionButton
        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
        isDisabled={!shouldRenderFullDescription}
      >
        <StyledTagDescriptionDiv
          ref={descriptionRef}
          $isDescriptionExpanded={isDescriptionExpanded || !shouldRenderFullDescription}
        >
          {tag.description}
        </StyledTagDescriptionDiv>
        {shouldRenderFullDescription && (
          <StyledTagDescriptionIcon
            $isDescriptionExpanded={isDescriptionExpanded}
            name="chevronDownSmall"
            color={theme.greys.shade70}
          />
        )}
      </StyledTagDescriptionButton>

      <StyledProgressBarContainerDiv>
        {/*
          We do this because the progress bar here is out of 100, but "done" is when we have 10 samples, so we have to scale it.
          We pass a `key` prop to reset the component when the tag changes to avoid animating from 100 -> 0.
         */}
        <ProgressBar
          size={VisualSizesEnum.SMALL}
          key={tag.id}
          progress={progress * AI_TAGGING_SAMPLES_NEEDED}
        />
      </StyledProgressBarContainerDiv>
      <StyledProgressTextDiv>
        {progress}/{AI_TAGGING_SAMPLES_NEEDED} approved
      </StyledProgressTextDiv>
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderLoadingState(onCancel: () => void) {
  return (
    <StyledWrapperDiv>
      <StyledHeaderStaticPartDiv>
        <StyledHeaderButton key="close" onClick={onCancel}>
          <Icon name="close" />
          <VisuallyHidden>
            <Trans id="dialogheader-close">Close</Trans>
          </VisuallyHidden>
        </StyledHeaderButton>
        <StyledTitleH2>{renderTitle()}</StyledTitleH2>

        <StyledTagSubheaderDiv>
          <Skeleton width="100%" height="22px" borderRadius="6px" scheme={SkeletonColorSchemesEnum.GREY10} />
        </StyledTagSubheaderDiv>
      </StyledHeaderStaticPartDiv>
      <StyledProgressBarContainerDiv>
        <Skeleton width="100%" height="30px" borderRadius="6px" scheme={SkeletonColorSchemesEnum.GREY10} />
      </StyledProgressBarContainerDiv>
    </StyledWrapperDiv>
  );
}

function renderTitle() {
  return <Trans id="ai-harvesting-header-title">Approve related messages</Trans>;
}
